// The words on the latest-game card, after v1's LatestGameModel: pure
// functions over the game record and its PGN headers, so they can be tested
// without a store or a browser.
import { type GameFacts, type GameSummary, type Phase } from '@chess/api-model';

export type Outcome = 'won' | 'lost' | 'drawn' | undefined;

/** Which side the owner played, if they played at all. */
export function ownerSide(game: Pick<GameSummary, 'owner' | 'white' | 'black'>): 'white' | 'black' | undefined {
  const owner = game.owner.toLowerCase();
  if (game.white.name.toLowerCase() === owner) return 'white';
  if (game.black.name.toLowerCase() === owner) return 'black';
  return undefined;
}

/** How the game went for its owner; undefined when that cannot be said. */
export function outcome(game: Pick<GameSummary, 'owner' | 'white' | 'black' | 'result'>): Outcome {
  if (game.result === '1/2-1/2') return 'drawn';
  const side = ownerSide(game);
  if (!side || game.result === '*') return undefined;
  return (side === 'white') === (game.result === '1-0') ? 'won' : 'lost';
}

/** The other player's name, when the owner is one of the two. */
export function opponent(game: Pick<GameSummary, 'owner' | 'white' | 'black'>): string | undefined {
  const side = ownerSide(game);
  if (side === 'white') return game.black.name;
  if (side === 'black') return game.white.name;
  return undefined;
}

/** "Blitz 5+0" from the record's time control and the PGN's time class, if any. */
export function pace(timeControl: string | undefined, timeClass: string | undefined): string | undefined {
  const cls = timeClass ? timeClass.charAt(0).toUpperCase() + timeClass.slice(1) : undefined;
  if (cls && timeControl) return `${cls} ${timeControl}`;
  return cls ?? timeControl;
}

/** "You vs. m_petrosyan_92 · Blitz 5+0 · Lost" — the reader first, as v1 has it. */
/** "You vs. rival · Blitz 5+0": who and at what pace; the result is the card's badge (in the headline until 2026-10-08). */
export function headline(game: GameSummary, timeClass?: string): string {
  const parts: string[] = [];
  const other = opponent(game);
  parts.push(other ? `You vs. ${other}` : `${game.white.name} vs. ${game.black.name}`);
  const p = pace(game.timeControl, timeClass);
  if (p) parts.push(p);
  return parts.join(' · ');
}

/**
 * The game's title under Home's game strip (2026-10-10): "EdBlackKnight
 * 1700 vs rival 1722", the owner first (the username in Settings, which the
 * game's records are imported for, as chess.com capitalises it in the game),
 * each player's rating after their name (the ratings being the better
 * reference to read the game by), then the pace and the date. The result is
 * the title's badge. When the owner played neither side, White stands
 * first and Black second.
 */
export interface GameTitle {
  /** The owner's name as the game writes it (the Settings username), or White's when the owner played neither. */
  me: string;
  /** The owner's rating when the game was played (White's when the owner played neither). */
  myRating?: number;
  /** The opponent's name (Black's when the owner played neither). */
  name: string;
  /** The opponent's rating when the game was played. */
  rating?: number;
  /** "Blitz 5+0". */
  pace?: string;
  /** "Oct 9". */
  date: string;
}

export function gameTitle(game: GameSummary, timeClass?: string): GameTitle {
  const side = ownerSide(game);
  const mine = side === 'black' ? game.black : game.white;
  const other = side === 'black' ? game.white : game.black;
  const when = new Date(game.playedAt);
  return {
    me: mine.name,
    myRating: mine.rating,
    name: other.name,
    rating: other.rating,
    pace: pace(game.timeControl, timeClass),
    date: Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
}

/**
 * "You lost by resignation" from chess.com's Termination header
 * ("A won by resignation", "Game drawn by agreement"), from the owner's
 * side; without a header, the bare outcome.
 */
export function terminationText(game: GameSummary, termination: string | undefined): string {
  const how = outcome(game);
  if (termination) {
    const won = /^(.+?) won by (.+)$/i.exec(termination);
    if (won) {
      const [, winner, by] = won;
      const side = ownerSide(game);
      const ownerWon = side !== undefined && winner.toLowerCase() === (side === 'white' ? game.white : game.black).name.toLowerCase();
      if (side) return `You ${ownerWon ? 'won' : 'lost'} by ${by}`;
      return `${winner} won by ${by}`;
    }
    const drawn = /^game drawn by (.+)$/i.exec(termination);
    if (drawn) return `Drawn by ${drawn[1]}`;
    return termination;
  }
  if (how === 'won') return 'You won';
  if (how === 'lost') return 'You lost';
  if (how === 'drawn') return 'Drawn';
  return game.result === '*' ? 'Unfinished' : game.result;
}

/**
 * The opening's name from chess.com's ECOUrl header, whose last segment is
 * the name followed by the moves: "Philidor-Defense-Nimzowitsch-Variation-4.dxe5-Nxe4"
 * becomes "Philidor Defense Nimzowitsch Variation".
 */
export function openingText(headers: Record<string, string>): string | undefined {
  const url = headers.ECOUrl;
  if (url) {
    const slug = url.split('/').filter(Boolean).pop() ?? '';
    const words: string[] = [];
    for (const w of slug.split('-')) {
      if (/^\d+\.{1,3}/.test(w) || /^[a-h]x?[a-h]?[1-8]/.test(w) && /\d/.test(w) && w === w.toLowerCase()) break;
      if (/^[KQRBN]?[a-h]?x?[a-h][1-8]/.test(w) && /\d/.test(w)) break;
      if (/^O-O/.test(w)) break;
      words.push(w);
    }
    if (words.length) return words.join(' ');
  }
  return headers.Opening;
}

/** "38 moves · You lost by resignation · Sicilian Defence". */
export function detail(game: GameSummary, plies: number, headers: Record<string, string>): string {
  const parts: string[] = [];
  const moves = Math.ceil(plies / 2);
  if (plies > 0) parts.push(moves === 1 ? '1 move' : `${moves} moves`);
  parts.push(terminationText(game, headers.Termination));
  const opening = openingText(headers);
  if (opening) parts.push(opening);
  return parts.join(' · ');
}

/** "today", "yesterday", "3 days ago", "2 weeks ago", "4 months ago", or the date. */
export function recency(playedAt: string, now: Date = new Date()): string {
  const then = new Date(playedAt);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The PGN's time class header, if chess.com wrote one into the record. */
export function timeClassOf(headers: Record<string, string>): string | undefined {
  // chess.com carries the class only in the API, not the PGN; TimeControl "300" means blitz.
  const tc = headers.TimeControl;
  if (!tc) return undefined;
  const base = Number(tc.split('+')[0]);
  if (!Number.isFinite(base)) return tc.includes('/') ? 'daily' : undefined;
  if (base < 180) return 'bullet';
  if (base < 600) return 'blitz';
  if (base < 1800) return 'rapid';
  return 'classical';
}

/** The engine's verdict on one game, from its facts: the score of each stage and the reader's blunders and misses. */
export interface GameMetrics {
  /** Pawns, the reader's side: the score after the opening, then the change across the middlegame and the ending; a stage the game did not reach is absent. */
  stages: Partial<Record<Phase, number>>;
  blunders: number;
  misses: number;
  /** The reader's accuracy over the game, 0–100 (`GameFacts.accuracy`); absent when the scan gave none. */
  accuracy?: number;
  /** The reader's accuracy over each stage, 0–100 (`GameFacts.stageAccuracy`); a stage with too few of the reader's moves is absent. */
  stageAccuracy?: Partial<Record<Phase, number>>;
  /** The ply each stage starts on (`GameFacts.stageCurves[stage].fromPly + 1`, the review's first position of the stage, as the per-game sections' link); a stage without a curve is absent. */
  stageStart: Partial<Record<Phase, number>>;
}

/** The game's metrics, or undefined until its scan is complete. */
export function gameMetrics(
  facts: Pick<GameFacts, 'scanned' | 'stageSwings' | 'blunders' | 'misses' | 'accuracy' | 'stageAccuracy' | 'stageCurves'> | undefined,
): GameMetrics | undefined {
  if (!facts?.scanned) return undefined;
  const stageStart: Partial<Record<Phase, number>> = {};
  for (const [phase, curve] of Object.entries(facts.stageCurves ?? {}) as Array<[Phase, { fromPly: number } | undefined]>) {
    if (curve) stageStart[phase] = curve.fromPly + 1;
  }
  const metrics: GameMetrics = { stages: { ...facts.stageSwings }, blunders: facts.blunders ?? 0, misses: facts.misses ?? 0, stageStart };
  if (facts.accuracy !== undefined) metrics.accuracy = facts.accuracy;
  if (facts.stageAccuracy !== undefined) metrics.stageAccuracy = { ...facts.stageAccuracy };
  return metrics;
}

/** "2 blunders", "1 blunder", "No blunders"; the same for misses. */
export function countText(count: number, noun: 'blunder' | 'miss'): string {
  const plural = noun === 'miss' ? 'misses' : 'blunders';
  if (count === 0) return `No ${plural}`;
  return count === 1 ? `1 ${noun}` : `${count} ${plural}`;
}
